import { Component, DestroyRef, Injector, OnInit, OnDestroy, TemplateRef, afterNextRender, effect, inject, DOCUMENT, signal, viewChild } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Observable } from "rxjs";
import { Router, Scroll, RouterOutlet } from "@angular/router";
import { filter, take, switchMap } from "rxjs/operators";
import { AuthService } from "@auth0/auth0-angular";
import { AnalyticsIdentityService, AnalyticsService, EVENT_NAMES } from "@uplift-sdk";
import { UserIdleService } from "@sdk/lib/user-idle/user-idle.service";
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { LOGIN_PATH } from "@sdk/lib/values/shared-paths";
import { ViewportScroller } from "@angular/common";
import { Store } from "@ngxs/store";
import { GroupState } from "./states/group-state";
import { Group } from "@sdk/lib/types/group";
import { LocalizationService } from "./services/localization.service";
import { TranslocoPipe } from "@jsverse/transloco";
import { WebinarPrepareLoaderComponent } from "../../../../libs/uplift-sdk/src/lib/components/webinar-prepare-loader/webinar-prepare-loader.component";
import { AssistantChatbotComponent } from "./shared/assistant-chatbot/assistant-chatbot.component";
import { BrandFaviconService } from "./services/brand-favicon.service";

@Component({
    selector: "app-root",
    templateUrl: "./app.component.html",
    imports: [RouterOutlet, TranslocoPipe, WebinarPrepareLoaderComponent, AssistantChatbotComponent]
})
export class AppComponent implements OnInit, OnDestroy {
  private _router = inject(Router);
  private _ngModelService = inject(NgbModal);
  private _userIdleService = inject(UserIdleService);
  private _destroyRef = inject(DestroyRef);
  private _injector = inject(Injector);
  private _auth = inject(AuthService);
  // Instantiating AnalyticsIdentityService keeps Sentry/PostHog identity in sync with Auth0.
  private _analyticsIdentity = inject(AnalyticsIdentityService);
  private _analytics = inject(AnalyticsService);
  private viewportScroller = inject(ViewportScroller);
  private document = inject<Document>(DOCUMENT);
  private _localization = inject(LocalizationService);

  public readonly _containerRef = viewChild("userIdleContainer", { read: TemplateRef });
  private _timeoutDialog: NgbModalRef;
  private _store = inject(Store);
  private readonly _brandFavicon = inject(BrandFaviconService);
  private readonly _group = this._store.selectSignal<Group>(GroupState as any);
  private readonly _faviconByBrand = effect(() =>
    this._brandFavicon.apply(this._group()?.course?.course_brand),
  );
  readonly assistantUrl = signal("");
  private _ariaHiddenObserver?: MutationObserver;

  ngOnInit() {
    this._installAriaHiddenInertMirror();

    this._auth.error$.pipe(
      switchMap(() => this._auth.isAuthenticated$.pipe(take(1))),
      filter((isAuthenticated) => isAuthenticated),
      takeUntilDestroyed(this._destroyRef),
    ).subscribe(() => {
      this._auth.logout({
        logoutParams: {
          returnTo: window.location.origin + "/" + LOGIN_PATH,
        },
      });
    });

    this._router.events
      .pipe(
        filter((e): e is Scroll => e instanceof Scroll),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe((e) => {
        afterNextRender(
          () => {
            if (e.position) {
              this.viewportScroller.scrollToPosition(e.position);
            } else if (e.anchor) {
              this.viewportScroller.scrollToAnchor(e.anchor);
            } else {
              this.viewportScroller.scrollToPosition([0, 0]);
            }
          },
          { injector: this._injector },
        );
      });

    (this._store.select(GroupState as any) as Observable<Group>)
      .pipe(
        filter((group) => !!group?.course),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe((group) => {
        this.assistantUrl.set(group.course.assistant_url || "");

        this._localization.applyCourseLanguage(group.course.language);

        const isDarkMode = localStorage.getItem("sn-dark-mode") === "true";
        if (isDarkMode) {
          this.document.documentElement.setAttribute("data-theme", "dark");
        } else {
          this.document.documentElement.removeAttribute("data-theme");
        }
        this._syncThemeColor(isDarkMode);
      });

    this._userIdleService
      .onTimerStart()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((time) => {
        if (time === 1) {
          this._timeoutDialog = this._ngModelService.open(
            this._containerRef(),
            { backdrop: "static" },
          );
        } else if (time === 60) {
          this._analytics.track(EVENT_NAMES.session_timed_out, {
            idle_seconds: 1200,
          });
          this.logout("idle");
        }
      });
  }

  keepOpen() {
    this._timeoutDialog?.dismiss();
    this._userIdleService.stopTimer();
    this._userIdleService.resetTimer();
    this._userIdleService.stopWatching();
    this._userIdleService.startWatching();
  }

  logout(reason: "user" | "idle" = "user") {
    if (reason === "user") {
      this._analytics.track(EVENT_NAMES.logout_clicked);
    }
    this._timeoutDialog?.dismiss();
    this._userIdleService.stopTimer();
    this._userIdleService.resetTimer();
    this._userIdleService.stopWatching();
    localStorage.removeItem("sn-dark-mode");
    this._localization.reset();
    this._auth.logout({
      logoutParams: {
        returnTo: window.location.origin + "/" + LOGIN_PATH,
      },
    });
  }

  private _syncThemeColor(isDark: boolean): void {
    const meta = this.document.querySelector('meta[name="theme-color"]');
    if (meta) {
      meta.setAttribute("content", isDark ? "#1c1e27" : "#ffffff");
    }
  }

  ngOnDestroy() {
    this._ariaHiddenObserver?.disconnect();
  }

  // Mirror aria-hidden="true" -> inert so backgrounded content (e.g. behind an open
  // ng-bootstrap modal) is removed from the focus order, not just hidden from AT.
  // Without this, Tab still reaches controls under the modal (SC 2.4.11 / focus trap).
  private _installAriaHiddenInertMirror(): void {
    if (typeof MutationObserver === "undefined") return;
    const sync = (el: Element) => {
      if (el.getAttribute("aria-hidden") === "true") {
        if (!el.hasAttribute("inert")) el.setAttribute("inert", "");
      } else if (el.hasAttribute("inert")) {
        el.removeAttribute("inert");
      }
    };
    this.document.querySelectorAll("[aria-hidden]").forEach(sync);
    this._ariaHiddenObserver = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type !== "attributes" || m.attributeName !== "aria-hidden") continue;
        sync(m.target as Element);
      }
    });
    this._ariaHiddenObserver.observe(this.document.body, {
      attributes: true,
      attributeFilter: ["aria-hidden"],
      subtree: true,
    });
  }
}
